import type {
	FilterRuleModel,
	Metric,
	OptimizationIDModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'
import type { MetricsDataUnit } from '../model/types'

import { any, filter, pipe, prop } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useMetricsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { DeltaValue } from '@/shared/components/ui/delta-value'
import {
	DescriptionDetail,
	DescriptionGroup,
	DescriptionList,
	DescriptionTerm,
} from '@/shared/components/ui/description-list'
import { Skeleton } from '@/shared/components/ui/skeleton'

import {
	formatMericValue,
	formatTotalDiffValue,
	getMetricCurrency,
	getMetricDiffByType,
	getTypesByMetricSchema,
	getUnprefixedMetricKey,
} from '../lib/helpers'

export const MetricsSummaryWidget = ({
	isLoading: isDataLoading,
	queryParams,
}: {
	isLoading: boolean
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
}) => {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const metricsQuery = useMetricsQuery({
		select: pipe([filter(({ type }) => type === 'predict_default')]),
	})
	const interpretabilityStatisticQuery = useInterpretabilityStatisticQuery(
		{ ...queryParams!, currency },
		{
			refetchOnMount: true,
			staleTime: 0,
			enabled: !!queryParams && !!metricsQuery.data && !isDataLoading,
			select: (data) =>
				METRICS.map((metric) => {
					const value = extractMetricValues(
						(data as StatisticsInterpretabilityResponse)
							?.metrics as MetricsDataStatistic,
						metric
					)

					return getMetricValues({
						metric,
						data: value as unknown as MetricsDataUnit,
						metricsSchema: metricsQuery.data,
						currency,
					})
				}),
		}
	)
	const isLoading =
		any(prop('isLoading'), [metricsQuery, interpretabilityStatisticQuery]) ||
		isDataLoading
	const isError = any(prop('isError'), [
		metricsQuery,
		interpretabilityStatisticQuery,
	])
	const error = metricsQuery.error || interpretabilityStatisticQuery.error
	if (isLoading) {
		return (
			<div className='mt-2 flex w-full space-x-4'>
				<Skeleton className='h-10 w-24' />
				<Skeleton className='h-10 w-20' />
				<Skeleton className='h-10 w-20' />
			</div>
		)
	}
	if (isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{error?.message || t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<DescriptionList className='flex space-x-4'>
			{!!interpretabilityStatisticQuery.data &&
				interpretabilityStatisticQuery.data.map((item) => (
					<DescriptionGroup key={item.key}>
						<DescriptionTerm>
							{`${t(`metric.${item.key}`, { defaultValue: item.key })}`}
						</DescriptionTerm>
						<DescriptionDetail>
							<span className='text-lg font-semibold text-foreground'>
								{item.value}
							</span>
							<DeltaValue className='ml-1 text-sm' value={item.diffValue}>
								{formatTotalDiffValue(
									Number(item.diffValue),
									item.diffType,
									t,
									item.currency
								)}
							</DeltaValue>
						</DescriptionDetail>
					</DescriptionGroup>
				))}
		</DescriptionList>
	)
}
type MetricsDataStatistic = Record<string, MetricsDataUnit>

const METRICS = [
	'predict_revenue',
	'predict_gross_profit_margin',
	'predict_sales_items',
]
const getMetricValues = ({
	metric,
	data,
	metricsSchema,
	currency,
}: {
	metric: string
	data: MetricsDataUnit
	metricsSchema: Metric[]
	currency?: string
}) => {
	const metricSchema = metricsSchema?.find(
		({ name }) => name.split('.')[0] === `${metric}`
	)
	const { dataType, diffType } = getTypesByMetricSchema(metricSchema!)
	const diffValue = getMetricDiffByType(data, diffType)
	const key = getUnprefixedMetricKey(metric)
	const metricCurrency = getMetricCurrency(metricSchema, currency)

	return {
		key,
		value: formatMericValue({
			value: data.final || 0,
			type: dataType,
			currency: metricCurrency,
		}),
		diffType,
		diffValue,
		currency: metricCurrency,
	}
}
const extractMetricValues = (data: MetricsDataStatistic, metric: string) => ({
	init: data?.[`${metric}.init`],
	final: data?.[`${metric}.final`],
	optimal: data?.[`${metric}.optimal`],
})
